import { useEffect, useMemo, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import type { UnitTypesRes } from '@quick/go-api-types/money/unit-types';

import { useAppTranslation } from '@/app/i18n/client';
import { useFetch } from '@/layers/core/deal/services/useFetch';

import { useFormDataContext } from './FormDataContext';
import { SelectorVehicle } from './SelectorVehicle';
import type { IForm } from './types';

export const Vehicle = () => {
  const { watch } = useFormContext<IForm>();

  // It seems watch returns a reference to a mutable object. This is needed monitor the field
  const [additionalFields, update] = useState<Record<string, number>>({});
  useEffect(() => {
    const s = watch(({ additional_fields, packages }, { name }) => {
      if (!name?.includes('additional_fields') && !name?.includes('packages'))
        return;
      const fields = Object.entries(additional_fields ?? {}).reduce(
        (p, [k, v]) => {
          const key = k.replace('_', '');
          if (typeof v === 'boolean') {
            p[key] = +v;
            return p;
          }
          if (typeof v === 'string') return p;
          if (!v) return p;
          p[key] = Number(v.total) || 0; // Avoid NaN
          return p;
        },
        {} as Record<string, number>
      );
      const pFields = packages?.reduce(
        (p, pack) => {
          Object.entries(pack?.f ?? {}).forEach(([k, v]) => {
            const key = k.replace('_', '');
            // Package is not supposed to have booleans. String does not account for score
            if (typeof v === 'boolean' || typeof v === 'string') return;
            p[key] ??= 0;
            p[key] += Number(v?.total) || 0; // Prevent NaN
          });
          return p;
        },
        {} as Record<string, number>
      );
      update({ ...fields, ...pFields });
    });
    return () => s.unsubscribe();
  }, [watch]);

  // const checkAdditionalFields = Object.values(additionalFields).every(
  //   (value) => {
  //     if (typeof value === 'boolean') return true;
  //     return parseInt(value?.toString()) >= 0;
  //   }
  // );
  return <ReadyVehicle additionalFields={additionalFields} />;
};

const ReadyVehicle = ({
  additionalFields,
}: {
  additionalFields: { [key: string]: number };
}) => {
  const { t } = useAppTranslation(['common', 'demand']);

  const { register, watch } = useFormContext<IForm>();

  const uom = watch('uom');

  const { serviceType, cargoType: selectedLoadType } = useFormDataContext();

  const vehicles = useMemo(
    () =>
      selectedLoadType?.vehicle_types.map(({ id, name, additional_fields }) => {
        return { id, name, additional_fields };
      }),

    [selectedLoadType]
  );

  const { data: unitTypes } = useFetch<UnitTypesRes>({
    url: '/money/unit-types',
    params: { pagination: false },
    immutable: true,
  });

  const sortedVehicles = useMemo(
    () =>
      vehicles
        ?.map(({ additional_fields: vehicleAdditionals, ...rest }) => {
          // console.group(rest.name);
          const res = {
            ...rest,
            point: Object.entries(additionalFields).reduce(
              (prev, [fieldName, value]) => {
                // Search vehicle property
                const vehicleAdditional = vehicleAdditionals.find(
                  ({ id }) => id.toString() === fieldName.replace('_', '')
                );

                let fieldMax = vehicleAdditional?.additional_field_max ?? 0;
                // max value is in metric so we need to convert totals to metric
                if (uom === 'us') {
                  const unitType = unitTypes?.find(
                    ({ magnitude, default: def }) =>
                      def && magnitude === vehicleAdditional?.magnitude?.id
                  );
                  fieldMax *= Number(unitType?.conversion_factor_us) || 1;
                }

                // Calculate difference for that value
                const result = fieldMax - value;

                // console.log(
                //   `${fieldName}:\n${vehicleAdditional?.additional_field_max} - ${normalizedValue} = ${result}`
                // );

                return result * (result < 0 ? Infinity : 1) + prev;
              },
              0
            ),
          };
          // console.groupEnd(rest.name);
          return res;
        })
        .filter(({ point }) => point >= 0)
        .sort((a, b) => a?.point - b?.point),
    [vehicles, additionalFields, uom, unitTypes]
  );

  const separate = useMemo(
    () =>
      !!serviceType?.additional_fields?.filter(
        ({ field: { is_for_offer } }) => is_for_offer
      ).length && (sortedVehicles?.length ?? 0) > 1,
    [serviceType, sortedVehicles?.length]
  );

  if (!serviceType?.req_vehicle) return null;
  if (!sortedVehicles?.length)
    return (
      <p
        {...register('error' as 'stops', {
          required: true,
          shouldUnregister: true,
        })}
        tabIndex={0}
        className="col-start-1 col-end-[-1] py-4 text-center font-semibold text-red-500
          focus:text-base focus:font-bold focus:underline dark:text-red-300"
      >
        {t('demand:noVehicle')}
      </p>
    );
  return (
    <>
      {separate && (
        <div className="bg-background-light dark:bg-background-dark h-1 sm:col-span-2" />
      )}
      <SelectorVehicle vehicles={sortedVehicles} />
    </>
  );
};
